import "./comment.scss";

import { Button, message, Input } from "antd";
import { post } from "../../common/fetch";
import { useEffect, useState, useRef } from "react";
import { useQuery, intToChinese } from "../../common/utils";

import {
  PlusEvaluateItem,
  MinusEvaluateItem,
  plusScoreItem,
  minusScoreItem,
} from "./components/evaluateItem/evaluateItem";

interface evaluateFormProps {
  courseId: number;
  courseName: string;
  createTime: string;
  creator: number;
  evaluateType: number;
  formType: number;
  formName: string;
  id: number;
  instituteId: string;
  reviser: number;
  scoreValue: number;
  score?: number;
  scoreText?: string;
  courseEvaluateCountFlag?: number;
  evaluateFlag: number;
  status: number;
  updateTime: string;
  version: number;
}
interface evaluateItemProps {
  formId: number;
  fullItemName: string;
  id: number;
  name: string;
  plusScoreList?: plusScoreItem[];
  minusScoreList?: minusScoreItem[];
  scoreCalculateType: number;
  scoreValue: number;
  scoreValueText: string;
  showOrderInForm: number;
}

const { TextArea } = Input;

interface CommentProps {
  courseId?: string | null;
  evaluateFormId?: string | null;
  projectId?: string | null;
  topicId?: string | null;
  getVideoProgress?: () => number;
  getStudyProgress?: () => number;
}

/**
 * 暂存接口目前不会返回每一个加/减分项的明细。课程测评弹窗关闭时会被
 * destroyOnClose 卸载，因此在当前浏览器会话中保留一次成功暂存的明细，
 * 以便再次打开弹窗时可以继续编辑。
 */
interface ScoreDraft {
  plusScoreMap: Record<string, number>;
  minusScoreMap: Record<string, number>;
}

function normalizeScore(value: unknown, maxValue: unknown, fallback = 0): number {
  const max = Number(maxValue);
  const score = Number(value);
  const safeScore = Number.isFinite(score) ? score : fallback;
  const nonNegativeScore = Math.max(safeScore, 0);

  // 部分暂存数据未返回单项最高分。此时不能把上限误认为 0，否则 + / -
  // 会永久停留在 0；只有接口给出有效最高分时才进行上限约束。
  return Number.isFinite(max) && max >= 0
    ? Math.min(nonNegativeScore, max)
    : nonNegativeScore;
}

function getScoreMaximum(score: plusScoreItem | minusScoreItem): number | undefined {
  const configuredMaximum = Number(score.maxScoreValue);
  if (Number.isFinite(configuredMaximum) && configuredMaximum >= 0) {
    return configuredMaximum;
  }

  // 暂存后接口可能把 scoreValue 覆盖为当前得分；题干仍保留真实满分，例如“满分30分”。
  const text = `${score.fullContent || ""} ${score.scoreValueText || ""}`;
  const match = text.match(/满分\s*([\d.]+)\s*分/i) || text.match(/[（(]\s*([\d.]+)\s*分\s*[）)]/);
  if (match) {
    const maximum = Number(match[1]);
    if (Number.isFinite(maximum) && maximum >= 0) return maximum;
  }

  const scoreValue = Number(score.scoreValue);
  return Number.isFinite(scoreValue) && scoreValue >= 0 ? scoreValue : undefined;
}

function normalizeEvaluateItemList(value: unknown): evaluateItemProps[] {
  if (!Array.isArray(value)) return [];

  return value
    .filter((item): item is Record<string, any> => Boolean(item && typeof item === "object"))
    .map((item) => ({
      ...item,
      // Older gateways may serialize numeric enum values as strings.
      scoreCalculateType: Number(item.scoreCalculateType) === 2 ? 2 : 1,
      plusScoreList: Array.isArray(item.plusScoreList) ? item.plusScoreList : [],
      minusScoreList: Array.isArray(item.minusScoreList) ? item.minusScoreList : [],
    })) as evaluateItemProps[];
}

function getScoreDraftKey(
  courseId?: string | null,
  evaluateFormId?: string | null,
  projectId?: string | null,
  topicId?: string | null
): string {
  return ["evaluate-score-draft", courseId || "", evaluateFormId || "", projectId || "", topicId || ""].join(":");
}

function readScoreDraft(key: string): ScoreDraft | null {
  try {
    const value = window.sessionStorage.getItem(key);
    if (!value) return null;

    const draft = JSON.parse(value);
    if (!draft || typeof draft !== "object") return null;

    return {
      plusScoreMap: draft.plusScoreMap || {},
      minusScoreMap: draft.minusScoreMap || {},
    };
  } catch {
    return null;
  }
}

function saveScoreDraft(key: string, evaluateList: evaluateItemProps[]): void {
  const draft: ScoreDraft = { plusScoreMap: {}, minusScoreMap: {} };

  evaluateList.forEach((item) => {
    item.plusScoreList?.forEach((score) => {
      if (score.value !== undefined && score.value !== null) {
        draft.plusScoreMap[String(score.id)] = score.value;
      }
    });
    item.minusScoreList?.forEach((score) => {
      if (score.value !== undefined && score.value !== null) {
        draft.minusScoreMap[String(score.id)] = score.value;
      }
    });
  });

  try {
    window.sessionStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // 浏览器禁用存储时仍可正常完成本次评分。
  }
}

function clearScoreDraft(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // 浏览器禁用存储时无需额外处理。
  }
}

const Comment: React.FC<CommentProps> = (props) => {
  const [totalScore, setTotalScore] = useState(0);
  const [savingType, setSavingType] = useState<"submit" | "temporary" | null>(null);
  const [evaluateFormInfo, setEvaluateFormInfo] = useState<evaluateFormProps>();
  const [evaluateListInfo, setEvaluateListInfo] = useState<evaluateItemProps[]>(
    []
  );
  const [evaluateLoading, setEvaluateLoading] = useState(true);
  const [evaluateError, setEvaluateError] = useState("");

  let query = useQuery();
  const topicId = props.topicId ?? query.get("topicId");
  const projectId = props.projectId ?? query.get("projectId");
  const courseId = props.courseId ?? query.get("courseId");
  const evaluateFormId = props.evaluateFormId ?? query.get("evaluateFormId");
  const scoreDraftKey = getScoreDraftKey(courseId, evaluateFormId, projectId, topicId);

  const appraise: any = useRef(null);
  const [hasScoreDraft, setHasScoreDraft] = useState(false);
  const [allowResubmit, setAllowResubmit] = useState(false);
  // 暂存记录会被服务端标记为已评分，但它仍应允许用户继续修改。
  const isSubmitted = Number(evaluateFormInfo?.evaluateFlag) === 1 && !hasScoreDraft && !allowResubmit;
  const isSaving = savingType !== null;

  const getEvaluate = () => {
    if (!courseId || !evaluateFormId) {
      setEvaluateLoading(false);
      setEvaluateError("测评参数缺失，请关闭弹窗后重试");
      return;
    }

    setEvaluateLoading(true);
    setEvaluateError("");
    const projectQuery = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
    post(`/shop/api/evaluate/${courseId}/${evaluateFormId}${projectQuery}`, {}, true).then(
      (res) => {
        if (res.code !== 0) throw new Error(res.msg || "获取测评信息失败");

        const responseData = res.data;
        const evaluateInfo = responseData?.evaluateInfo;
        const evaluateForm = evaluateInfo?.evaluateForm;
        if (!evaluateForm) throw new Error("测评表单信息为空");

        const evaluateItemList = normalizeEvaluateItemList(evaluateInfo.evaluateItemList);
        // 已提交记录必须优先于本地暂存草稿，避免旧草稿重新开启评分编辑。
        const scoreDraft = Number(evaluateForm.evaluateFlag) === 1 ? null : readScoreDraft(scoreDraftKey);
        const initializedList = evaluateItemList.map((item) => ({
          ...item,
          plusScoreList: item.plusScoreList?.map((score) => {
            const nextScore = { ...score };
            const maxScoreValue = getScoreMaximum(nextScore);
            const savedValue = nextScore.value ?? nextScore.score ?? nextScore.userScore ?? nextScore.scoreValue;
            nextScore.maxScoreValue = maxScoreValue;
            nextScore.value = normalizeScore(
              scoreDraft?.plusScoreMap[String(nextScore.id)] ??
                (savedValue === undefined || savedValue === null ? nextScore.scoreValue : savedValue),
              maxScoreValue,
            );
            return nextScore;
          }),
          minusScoreList: item.minusScoreList?.map((score) => {
            const nextScore = { ...score };
            const maxScoreValue = getScoreMaximum(nextScore);
            const savedValue = nextScore.value ?? nextScore.score;
            nextScore.maxScoreValue = maxScoreValue;
            nextScore.value = normalizeScore(
              scoreDraft?.minusScoreMap[String(nextScore.id)] ?? savedValue,
              maxScoreValue
            );
            return nextScore;
          }),
        }));

        setHasScoreDraft(Boolean(scoreDraft));
        setEvaluateFormInfo({
          ...evaluateForm,
          courseId: responseData.courseId,
          courseName: responseData.courseName,
          formType: Number(evaluateForm.formType),
          courseEvaluateCountFlag: Number(responseData.courseEvaluateCountFlag ?? evaluateForm.courseEvaluateCountFlag),
        });
        calcScore(initializedList);
        setEvaluateListInfo(initializedList);
        setEvaluateLoading(false);
      }
    ).catch((error) => {
      setEvaluateLoading(false);
      setEvaluateError(error?.message || "获取测评信息失败，请稍后重试");
      setEvaluateListInfo([]);
    });
  };

  useEffect(() => {
    getEvaluate();
  }, [courseId, evaluateFormId]); // eslint-disable-line react-hooks/exhaustive-deps

  function calcScore(evaluateList: evaluateItemProps[]): void {
    let score: number = 0;
    evaluateList.forEach((item) => {
      if (item.scoreCalculateType === 1) {
        item.plusScoreList?.forEach((plusEvaluateItem) => {
          score += plusEvaluateItem?.value || 0;
        });
      } else {
        item.minusScoreList?.forEach((minusEvaluateItem) => {
          score -= minusEvaluateItem?.value || 0;
        });
      }
    });

    setTotalScore(score);
  }

  function onChangeValue(
    list: number,
    item: number,
    type: number,
    value: any
  ): void {
    if (isSubmitted) return;

    setEvaluateListInfo((prevCount: evaluateItemProps[]) => {
      const newCount: evaluateItemProps[] = [...prevCount];
      let data: any =
        newCount[list].scoreCalculateType === 1
          ? newCount[list].plusScoreList?.[item]
          : newCount[list].minusScoreList?.[item];
      const maxScoreValue = getScoreMaximum(data);

      switch (type) {
        case 0:
          data.value = maxScoreValue ?? 0;
          break;

        case 1:
          data.value = normalizeScore(normalizeScore(data.value, maxScoreValue) + 1, maxScoreValue);
          break;

        case 2:
          data.value = normalizeScore(normalizeScore(data.value, maxScoreValue) - 1, maxScoreValue);
          break;

        case 3:
          data.value = 0;
          break;

        case 4:
          data.value = value === undefined || value === null ? undefined : normalizeScore(value, maxScoreValue);
          break;
      }
      calcScore(newCount);
      return newCount;
    });
  }

  /**
   * 保存按钮点击
   *
   */
  function buildEvaluateParam() {
    const param: any = {
      evaluateRecord: {
        projectId: projectId || undefined,
        topicId: topicId || undefined,
        courseId: evaluateFormInfo?.courseId,
        formId: evaluateFormInfo?.id,
        comment: appraise.current?.resizableTextArea?.textArea?.value,
      },
      evaluateRecordDetailList: [],
    };

    evaluateListInfo.forEach((item) => {
      if (item.scoreCalculateType === 1) {
        item.plusScoreList?.forEach((plusScore: plusScoreItem) => {
          param.evaluateRecordDetailList.push({
            scoreCalculateType: item.scoreCalculateType,
            itemId: item.id,
            plusScoreId: plusScore.id,
            score: plusScore.value,
          });
        });
      } else {
        item.minusScoreList?.forEach((minusScore: minusScoreItem) => {
          param.evaluateRecordDetailList.push({
            scoreCalculateType: item.scoreCalculateType,
            itemId: item.id,
            minusScoreId: minusScore.id,
            score: minusScore.value,
          });
        });
      }
    });

    return param;
  }

  function onSubmitClickHandle() {
    if (isSaving || isSubmitted) return;

    const videoProgress = props.getVideoProgress?.() ?? 0;
    const studyProgress = props.getStudyProgress?.() ?? 0;
    if (studyProgress !== 100 && videoProgress < 90) {
      message.info("学习进度达到90%后才可提交测评");
      return;
    }

    setSavingType("submit");
    post("/shop/api/evaluate/record/save", buildEvaluateParam(), true)
      .then((res) => {
        if (res.code === 0) {
          clearScoreDraft(scoreDraftKey);
          setHasScoreDraft(false);
          setAllowResubmit(false);
          setEvaluateFormInfo((current) => current ? { ...current, evaluateFlag: 1 } : current);
          message.success("提交成功");
          // setTimeout(() => {
          //   window.opener = null;
          //   window.open("", "_self");
          //   window.close();
          // }, 3000);
        } else {
          message.error(res.msg);
        }
      })
      .catch((err) => {
        message.error("网络错误");
      })
      .finally(() => {
        setSavingType(null);
      });
  }

  function onTemporarySaveClickHandle() {
    if (isSaving || isSubmitted) return;

    setSavingType("temporary");
    post("/shop/api/evaluate/record/temporary/save", buildEvaluateParam(), true)
      .then((res) => {
        if (res.code === 0) {
          saveScoreDraft(scoreDraftKey, evaluateListInfo);
          setHasScoreDraft(true);
          message.success("暂存成功");
        } else {
          message.error(res.msg);
        }
      })
      .catch(() => {
        message.error("网络错误");
      })
      .finally(() => {
        setSavingType(null);
      });
  }

  return (
    <div className="comment">
      {evaluateLoading ? (
        <div className="comment_state">测评信息加载中...</div>
      ) : evaluateError ? (
        <div className="comment_state comment_state_error">{evaluateError}</div>
      ) : (
      <>
      <div className="comment_rateview">
        <div className="comment_rateview_header">
          <p>{evaluateFormInfo?.formName}</p>
          <p>课程：{evaluateFormInfo?.courseName}</p>
        </div>
        <div className="comment_rateview_container">
          {evaluateListInfo.map((item: evaluateItemProps, index: number) => {
            return (
              <div className="comment_rateview_item" key={index}>
                <h5 className="comment_rateview_i_title">
                  {item.fullItemName}
                </h5>
                <ul>
                  {item.scoreCalculateType === 1
                    ? item.plusScoreList?.map(
                        (plusScore: plusScoreItem, plusScoreIndex: number) => {
                          return (
                            <PlusEvaluateItem
                              key={plusScoreIndex}
                              baseInfo={plusScore}
                              disabled={isSubmitted}
                              showScoreResult={isSubmitted}
                              listIndex={index}
                              itemIndex={plusScoreIndex}
                              onChangeValue={onChangeValue}
                            />
                          );
                        }
                      )
                    : item.minusScoreList?.map(
                        (
                          minusScore: minusScoreItem,
                          minusScoreIndex: number
                        ) => {
                          return (
                            <MinusEvaluateItem
                              key={minusScoreIndex}
                              baseInfo={minusScore}
                              disabled={isSubmitted}
                              listIndex={index}
                              itemIndex={minusScoreIndex}
                              onChangeValue={onChangeValue}
                            />
                          );
                        }
                      )}
                </ul>
              </div>
            );
          })}

          {evaluateFormInfo?.formType !== 2 && (
            <div className="comment_rateview_item">
              <h5 className="comment_rateview_i_title">
                {intToChinese(evaluateListInfo.length + 1)}、测评意见
              </h5>
              <div>
                <TextArea disabled={isSubmitted} style={{ height: 100 }} ref={appraise} />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="comment_footer">
        <div className="comment_footer_wrapper">
          {evaluateFormInfo && isSubmitted && (
            <span className="text">
              总分：
              {evaluateFormInfo.scoreText ?? evaluateFormInfo.score ?? totalScore}
              分
            </span>
          )}
          {isSubmitted ? (
            <div className="actions">
              <Button className="submitted" disabled>已提交</Button>
              {Number(evaluateFormInfo?.courseEvaluateCountFlag) === 2 && (
                <Button type="primary" onClick={() => setAllowResubmit(true)}>重新评分</Button>
              )}
            </div>
          ) : (
            <div className="actions">
              <Button loading={savingType === "temporary"} disabled={isSaving} onClick={onTemporarySaveClickHandle}>
                暂存
              </Button>
              <Button className="btn" type="primary" loading={savingType === "submit"} disabled={isSaving} onClick={onSubmitClickHandle}>
                提交
              </Button>
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default Comment;
